/**
 * The element's own editor in a dialog - issue #110.
 *
 * A reviewer reading a merge discussion who finds the disputed element wrong can
 * fix it without leaving the page. This is the SAME form the element's page uses
 * (`EntityForm`), not a copy of it: it saves through the same route, to the
 * branch the screen is showing, and gates itself with the same `useCanWrite`.
 * The caller decides whether to offer the action at all; the form still refuses
 * a save it should not make.
 *
 * Resolving the thread is not done here. Fixing the element and judging that the
 * concern is settled are two acts, and the second belongs to the person.
 */

import { useEffect, useState } from 'react'
import Alert from '@mui/material/Alert'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import CircularProgress from '@mui/material/CircularProgress'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import { ApiError, api } from '../../api'
import { findKind } from '../../schema'
import type { EntityRow } from '../../schema'
import { useSchema } from '../../state/AppData'
import EntityForm from '../entity/EntityForm'
import type { EditTarget } from './elementThreads'

export default function EditElementDialog({
  target,
  onClose,
  onSaved,
}: {
  target: EditTarget
  onClose: () => void
  /** After a save, so the screen can re-read what it shows. The dialog stays open. */
  onSaved: () => void
}) {
  const schema = useSchema()
  const kind = findKind(schema, target.plural)
  const [row, setRow] = useState<EntityRow | null>(null)
  const [failure, setFailure] = useState('')

  useEffect(() => {
    let live = true
    api
      .get(target.plural, target.id)
      .then((fresh) => {
        if (live) setRow(fresh)
      })
      .catch((error: ApiError) => {
        if (live) setFailure(error.message)
      })
    return () => {
      live = false
    }
  }, [target.plural, target.id])

  return (
    <Dialog open onClose={onClose} maxWidth="md" fullWidth aria-labelledby="edit-element-title">
      <DialogTitle id="edit-element-title">Edit {target.label}</DialogTitle>
      <DialogContent dividers>
        {failure && <Alert severity="error">{failure}</Alert>}
        {!kind && !failure && <Alert severity="info">BEST has no editor for this kind of element.</Alert>}
        {kind && !row && !failure && (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress aria-label="Loading the element" />
          </Box>
        )}
        {kind && row && (
          <EntityForm
            kind={kind}
            row={row}
            onSaved={(fresh) => {
              setRow(fresh)
              onSaved()
            }}
          />
        )}
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  )
}
